/**
 * What the register may do right now (T07-33, design/05 §4, design/02 P6/§7, the offline contract
 * `docs/dev/pos-offline-contract.md` §1) — one table the sale screen, the menu, the status bar and
 * the 「詳細」 sheet all read:
 *
 * | | online | offline < 5 min (yellow) | offline > 5 min (red) |
 * |---|---|---|---|
 * | one-of-a-kind card (raw / graded) | ✓ | ✓ only if the copy says available, no listing on another channel, copy < 5 min old | ✗ |
 * | sealed / supplies (qty > 1) | ✓ | ✓ | ✓ (the server flags 要確認 when the copy is old) |
 * | cash | ✓ | ✓ | ✓ |
 * | card terminal (EDC) | ✓ | ✗ (`offline_allow_edc` flag, phase 1 off) | ✗ |
 * | store credit · 免税 · 返品/取消 · 買取 · 委託受付 · 閉店 · 出金 · 値引き · 保留 | ✓ | ✗ | ✗ |
 * | queue ≥ `offlineMaxQueueOps` (50) | — | ✗ no more sales | ✗ |
 *
 * Offline the tablet shows the 税込 prices of its copy and the **sum** only — the tax is computed by
 * tax-service when the sale is uploaded (「税額はオンライン復帰後に確定」).
 */
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useState } from "react";

import { usePos, useRegister } from "@/app/context";
import type { SnapshotItem } from "@/lib/db";

import { useConnectivity, type Connectivity } from "./connectivity";

export type OfflineAction =
  | "edc"
  | "store_credit"
  | "tax_free"
  | "return"
  | "void"
  | "buylist"
  | "consignment"
  | "close_shift"
  | "pay_out"
  | "discount"
  | "hold"
  | "pickup";

export const OFFLINE_BLOCKED: readonly OfflineAction[] = [
  "store_credit",
  "tax_free",
  "return",
  "void",
  "buylist",
  "consignment",
  "close_shift",
  "pay_out",
  "discount",
  "hold",
  "pickup",
];

/** Menu route → the action it needs online (the menu greys these out offline). */
export const ROUTE_ACTION: Readonly<Record<string, OfflineAction>> = {
  "/shift": "close_shift",
  "/receipts": "return",
  "/hold": "hold",
  "/returns": "return",
  "/pickup": "pickup",
  "/tax-free": "tax_free",
  "/buylist": "buylist",
  "/consignment": "consignment",
};

export type ItemRefusal = "sold" | "external_listing" | "copy_old" | "offline_long" | "queue_full";

export interface RulesInput {
  state: Connectivity;
  /** Age of the offline copy (ms), null = no copy. */
  snapshotAgeMs: number | null;
  maxMinutes: number;
  queueLength: number;
  maxQueue: number;
  allowEdc: boolean;
}

export interface OfflineRules {
  state: Connectivity;
  online: boolean;
  queueFull: boolean;
  /** Can this item of the offline copy be sold now? */
  canSell(item: Pick<SnapshotItem, "available" | "productType" | "hasExternalListing">): {
    ok: boolean;
    reason: ItemRefusal | null;
  };
  allowed(action: OfflineAction): boolean;
}

const ONE_OF_A_KIND: ReadonlySet<string> = new Set(["raw_single", "graded_single", "graded"]);

export function isOneOfAKind(productType: string): boolean {
  return ONE_OF_A_KIND.has(productType);
}

export function offlineRules(input: RulesInput): OfflineRules {
  const online = input.state === "online";
  const queueFull = !online && input.queueLength >= input.maxQueue;
  const copyFresh = input.snapshotAgeMs !== null && input.snapshotAgeMs < input.maxMinutes * 60_000;
  return {
    state: input.state,
    online,
    queueFull,
    canSell(item) {
      if (online) {
        return { ok: true, reason: null };
      }
      if (queueFull) {
        return { ok: false, reason: "queue_full" };
      }
      if (!item.available) {
        return { ok: false, reason: "sold" };
      }
      if (!isOneOfAKind(item.productType)) {
        return { ok: true, reason: null };
      }
      if (input.state === "offline_long") {
        return { ok: false, reason: "offline_long" };
      }
      if (item.hasExternalListing) {
        return { ok: false, reason: "external_listing" };
      }
      if (!copyFresh) {
        return { ok: false, reason: "copy_old" };
      }
      return { ok: true, reason: null };
    },
    allowed(action) {
      if (online) {
        return true;
      }
      if (action === "edc") {
        return input.allowEdc;
      }
      return !OFFLINE_BLOCKED.includes(action);
    },
  };
}

/** Live rules of this register (connection + copy age + queue length). */
export function useOfflineRules(): OfflineRules & { snapshotAgeMs: number | null; ready: boolean } {
  const { db } = usePos();
  const register = useRegister();
  const connectivity = useConnectivity();
  const locationId = register.data?.register.locationId;
  // "loading" until IndexedDB answered — a scan waits for it (a card is never refused as "old copy"
  // only because the copy was not read yet)
  const meta = useLiveQuery(
    async () =>
      (await (locationId
        ? db.snapshotMeta.get(locationId)
        : db.snapshotMeta.toCollection().first())) ?? null,
    [db, locationId],
    "loading" as const,
  );
  const queueLength = useLiveQuery(
    () => db.opQueue.where("kind").equals("sale_cash").count(),
    [db],
  );
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(timer);
  }, []);
  const copy = meta === "loading" ? null : meta;
  const snapshotAgeMs = copy ? Math.max(0, now - copy.fetchedAt) : null;
  const rules = offlineRules({
    state: connectivity.state,
    snapshotAgeMs,
    maxMinutes: register.data?.rules.offlineMaxMinutes ?? 5,
    queueLength: queueLength ?? 0,
    maxQueue: register.data?.rules.offlineMaxQueueOps ?? 50,
    allowEdc: register.data?.features.offlineAllowEdc ?? false,
  });
  return { ...rules, snapshotAgeMs, ready: meta !== "loading" && queueLength !== undefined };
}
